import { Menu } from '@base-ui/react/menu'
import { Link, useNavigate } from '@tanstack/react-router'
import { authClient, signOut } from '#lib/auth'
import { trpc } from '#lib/trpc'

function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
}

/** Avatar button in the nav that opens a dropdown with profile, admin, and sign-out links. */
export function UserMenu() {
  const navigate = useNavigate()
  const { data: me } = trpc.profile.me.useQuery()
  const { data: session } = authClient.useSession()

  const isAdmin = me?.role === 'admin'
  const name = session?.user.name ?? ''
  const initials = getInitials(name)

  async function handleSignOut() {
    await signOut({ fetchOptions: { onSuccess: () => navigate({ to: '/login' }) } })
  }

  return (
    <Menu.Root>
      <Menu.Trigger
        className="w-9 h-9 rounded-full bg-terracotta-500 text-white text-sm font-medium flex items-center justify-center cursor-pointer hover:bg-terracotta-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500 focus-visible:ring-offset-2"
        aria-label={`Account menu for ${name}`}
      >
        {initials}
      </Menu.Trigger>

      <Menu.Portal>
        <Menu.Positioner side="bottom" align="end" sideOffset={8}>
          <Menu.Popup className="min-w-44 rounded-2xl bg-white shadow-warm-lg border border-cream-200 py-1.5 focus:outline-none data-[open]:animate-in data-[open]:fade-in-0 data-[open]:zoom-in-95 data-[closed]:animate-out data-[closed]:fade-out-0 data-[closed]:zoom-out-95">
            <div className="px-3 py-2 border-b border-cream-100 mb-1">
              <p className="text-sm font-medium text-warm-gray-800 truncate">{name}</p>
              <p className="text-xs text-warm-gray-400 truncate">{session?.user.email}</p>
            </div>

            <Menu.Item
              className="flex w-full items-center px-3 py-2 text-sm text-warm-gray-700 hover:bg-cream-50 cursor-pointer focus:outline-none focus:bg-cream-100 transition-colors"
              render={<Link to="/profile" />}
            >
              Profile
            </Menu.Item>

            {isAdmin ? (
              <Menu.Item
                className="flex w-full items-center px-3 py-2 text-sm text-warm-gray-700 hover:bg-cream-50 cursor-pointer focus:outline-none focus:bg-cream-100 transition-colors"
                render={<Link to="/admin" />}
              >
                Admin Dashboard
              </Menu.Item>
            ) : null}

            <div className="border-t border-cream-100 mt-1 pt-1">
              <Menu.Item
                className="flex w-full items-center px-3 py-2 text-sm text-warm-gray-500 hover:bg-cream-50 cursor-pointer focus:outline-none focus:bg-cream-100 transition-colors"
                onClick={handleSignOut}
              >
                Sign out
              </Menu.Item>
            </div>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  )
}
